'use client'

import { ReactSelectMenu } from '@repo/ui/ReactSelectMenu'
import { useBankFeedsStore } from '@repo/store/useBankFeedsStore'
import { useEffect, useState, useMemo } from 'react'

export const UnpaidBillsSelect = ({
  testData = null,
  value = null,
  onChange = (val) => {
    console.log(val)
  },
  sx,
  size = 'small',
  required,
  label,
  clientNo,
  disabled
}: UnpaidBillsSelectProps) => {
  const { fetchUnpaidBillsByClientNo, unpaidBills } = useBankFeedsStore()

  const VALUE_KEY = 'ledgerPostingId'

  useEffect(() => {
    if (testData || !clientNo || disabled) return
    fetchUnpaidBillsByClientNo({ clientNo: clientNo })
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [testData, clientNo])

  const formatLabel = (x: any) => {
    const toYMD = (s: string) => String(s).match(/^(\d{4}-\d{2}-\d{2})/)?.[1] ?? ''

    const detail = x.postingDetail ?? ''
    const date = toYMD(x.postingDate)
    const id = x.postingReference ?? '—'
    return `${clientNo}${x.matterNo ? `-${x.matterNo}` : ''} - ${date} - ${id} - ${detail}`
  }

  const options = useMemo(() => {
    const source: any[] = ((disabled ?? !clientNo) ? [] : (testData ?? unpaidBills)) as any[]
    return source.map((x) => ({
      ...x,
      __label: formatLabel(x)
    }))
  }, [testData, unpaidBills, clientNo, disabled])

  const toId = (val: any): string | null => {
    if (val == null) return null
    if (typeof val === 'string' || typeof val === 'number') return String(val)
    return val?.[VALUE_KEY] ? String(val[VALUE_KEY]) : null
  }

  const resolvedValue = useMemo(() => {
    if (disabled || !clientNo) return null
    const id = toId(value)
    if (!id) return null
    return options.find((o) => String(o[VALUE_KEY]) === id) ?? null
  }, [value, options])

  const handleChange = (next: any) => {
    if (disabled || !clientNo) return
    const id = toId(next)
    const obj = id ? (options.find((o) => String(o[VALUE_KEY]) === id) ?? next) : next
    onChange(obj)
  }

  return (
    <div data-testid="UnpaidBillsSelect" style={{ width: sx?.width || '100%', maxWidth: sx?.maxWidth || '100%' }}>
      <ReactSelectMenu
        disabled={disabled}
        idLabelKey="name"
        items={options}
        label={label ?? 'Unpaid Bills'}
        labelKey="__label"
        name="unpaidBills"
        onChange={handleChange}
        required={required}
        searchable
        showIdInResults={false}
        size={size}
        sx={{ [`& > div`]: { left: 0, width: '100%' } }}
        value={resolvedValue}
        valueKey={VALUE_KEY}
      />
    </div>
  )
}

type UnpaidBillsSelectProps = {
  testData?: any
  value?: any
  onChange?: (val: any) => void
  sx?: any
  size?: 'small' | 'medium'
  required?: boolean
  label?: string
  clientNo: string
  disabled?: boolean
}
